import { useState, useCallback } from 'react';
import { usePymbleApi } from './usePymbleApi';
import { useMyDetails } from './useMyDetails';
import { PastoralNoteFile } from './usePastoralNotes';

const NOTES_CONTAINER = 'secure/notes';
const NOTES_COLLECTION = 'Note';
const NOTES_TAGS = 'Object,Note';

function makeUploadId(noteId: string): string {
	const d = new Date();
	const yyyymmdd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
	return `${noteId}-${yyyymmdd}`;
}

export function useNoteFileUpload() {
	const { callApi } = usePymbleApi();
	const { myDetails } = useMyDetails();
	const [isUploading, setIsUploading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Uploader's Edumate contact id, stamped onto new files so the backend records
	// UploadedBy (and resolves UploadedByDetail) when the note's Files array saves.
	const uploaderContactId = myDetails?.contact_id
		? parseInt(String(myDetails.contact_id), 10)
		: undefined;

	// Current user's display name/_id, used to show an optimistic "Uploaded by …"
	// caption immediately after upload — before the server-resolved
	// UploadedByDetail arrives on the next refetch.
	const uploaderName = [myDetails?.Salutation, myDetails?.Name]
		.filter(Boolean)
		.join(' ')
		.trim();
	const uploaderId: string | undefined = myDetails?._id;

	const uploadFile = useCallback(async (file: File, noteId: string): Promise<PastoralNoteFile | null> => {
		setIsUploading(true);
		try {
			const d = new Date();
			const formData = new FormData();
			formData.append('file', file);
			const endpoint = `/storage/file/?container=${NOTES_CONTAINER}&collection=${NOTES_COLLECTION}&secure=true&tags=${NOTES_TAGS}&id=${makeUploadId(noteId)}`;
			const response = await callApi('POST', endpoint, {
				data: formData,
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			if (response?.Result === 'Uploaded' && response.File) {
				return {
					name: response.File.Name,
					url: response.File.Url,
					type: response.File.Type,
					secure: response.File.Secure ?? true,
					container: response.File.Container ?? NOTES_CONTAINER,
					InternalName: response.File.InternalName,
					// Stamp the uploader so it's sent in the note's Files array on save;
					// prefer any value the server already resolved on the upload response.
					UploadedBy:
						response.File.UploadedBy ??
						(uploaderContactId !== undefined && !Number.isNaN(uploaderContactId)
							? uploaderContactId
							: undefined),
					// Fall back to "now" and the current user so the caption shows
					// immediately; server-resolved values replace these on refetch.
					UploadedAt: response.File.UploadedAt ?? d.toISOString(),
					UploadedByDetail:
						response.File.UploadedByDetail ??
						(uploaderName && uploaderId
							? { DescShort: myDetails?.Name ?? uploaderName, DescLong: uploaderName, _id: uploaderId }
							: undefined),
				};
			}
			return null;
		} catch {
			return null;
		} finally {
			setIsUploading(false);
		}
	}, [callApi, uploaderContactId, uploaderName, uploaderId, myDetails?.Name]);

	const deleteFile = useCallback(async (container: string, internalName: string): Promise<boolean> => {
		setIsDeleting(true);
		try {
			const response = await callApi('DELETE', `/storage/file/?container=${container}&id=${internalName}`);
			return response?.Result === 'Deleted' || response?.Result === 'NotFound';
		} catch {
			return false;
		} finally {
			setIsDeleting(false);
		}
	}, [callApi]);

	return { uploadFile, isUploading, deleteFile, isDeleting };
}
